import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "module-relationships"
    }}>{`Module relationships`}</h1>
    <p>{`Understanding how one module relates to another helps determine how it reacts to changes in that module.`}</p>
    <p>{`A single module can have the following types of relationships with another module:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`uses`}</strong>{`: module A uses module B if it invokes behavior of module B`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`reacts to`}</strong>{`: module A reacts to module B if its behavior is triggered by an event in module B without module B knowing about module A`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`customizes`}</strong>{`: module A customizes module B if it modifies the behavior of module B`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`implements`}</strong>{`: module A implements module B if it implements some, not necessarily all, behavior that is defined in module B`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`replaces`}</strong>{`: module A replaces module B if it provides its own version of the API exposed and implemented by module B`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "relationship-types-and-scenarios"
    }}>{`Relationship types and scenarios`}</h2>
    <h3 {...{
      "id": "a-uses-b-c-customizes-b"
    }}>{`A uses B, C customizes B`}</h3>
    <p>{`In a scenario where module A uses module B and module C customizes module B, the customizations in module C must not break the API of module B so that module A still functions properly in the face of these customizations.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "225px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "60%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/11afad1ae8dc01aa6e1deaf3a41dbb0f/89528/archi_first_relate.webp 225w"],
            "sizes": "(max-width: 225px) 100vw, 225px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/11afad1ae8dc01aa6e1deaf3a41dbb0f/0608e/archi_first_relate.png 225w"],
            "sizes": "(max-width: 225px) 100vw, 225px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/11afad1ae8dc01aa6e1deaf3a41dbb0f/0608e/archi_first_relate.png",
            "alt": "Module relationship scenarios: A uses B, C customizes B",
            "title": "Module relationship scenarios: A uses B, C customizes B",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h3 {...{
      "id": "a-reacts-to-b-c-customizes-b"
    }}>{`A reacts to B, C customizes B`}</h3>
    <p>{`Similarly, in a case where module A reacts to module B and module C customizes module B, the customizations in module C must not interfere with the events in module B that module A depends on.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "214px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "63.084112149532714%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/af7dd40c01e129e734b3393ef19e9473/74519/archi_second_relate.webp 214w"],
            "sizes": "(max-width: 214px) 100vw, 214px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/af7dd40c01e129e734b3393ef19e9473/5b0b3/archi_second_relate.png 214w"],
            "sizes": "(max-width: 214px) 100vw, 214px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/af7dd40c01e129e734b3393ef19e9473/5b0b3/archi_second_relate.png",
            "alt": "Module relationship scenarios: A reacts to B, C customizes B",
            "title": "Module relationship scenarios: A reacts to B, C customizes B",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h3 {...{
      "id": "a-and-c-customize-b"
    }}>{`A and C customize B`}</h3>
    <p>{`If both module A and C customize module B, be careful about how these customizations are implemented so that you avoid conflicts (see below).`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "210px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "70.95238095238095%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/7864536ecd7972eb18f72c1af49e3327/8519f/archi_third_relate.webp 210w"],
            "sizes": "(max-width: 210px) 100vw, 210px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/7864536ecd7972eb18f72c1af49e3327/7f56b/archi_third_relate.png 210w"],
            "sizes": "(max-width: 210px) 100vw, 210px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/7864536ecd7972eb18f72c1af49e3327/7f56b/archi_third_relate.png",
            "alt": "Module relationship scenarios: A and C customize B",
            "title": "Module relationship scenarios: A and C customize B",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h3 {...{
      "id": "a-replaces-b"
    }}>{`A replaces B`}</h3>
    <p>{`If module A replaces module B, it needs to be able to do so in such a way that other modules are not affected. That will mean not having direct hard dependencies on module B, but rather dependencies on a third module, module C, that both module A and B implement.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "235px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "88.08510638297872%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/1018574a4ac6c48911cad4e362cd9423/c30e5/archi_fourth_relate.webp 235w"],
            "sizes": "(max-width: 235px) 100vw, 235px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/php/static/1018574a4ac6c48911cad4e362cd9423/09918/archi_fourth_relate.png 235w"],
            "sizes": "(max-width: 235px) 100vw, 235px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/php/static/1018574a4ac6c48911cad4e362cd9423/09918/archi_fourth_relate.png",
            "alt": "Module relationship scenarios: A replaces B",
            "title": "Module relationship scenarios: A replaces B",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      